import { BanFieldsFragment } from '@/generated/graphql';
import { BannedPeopleList } from '@/components/bans/BannedPeopleList';
import { BanPersonDialog } from '@/components/bans/BanPersonDialog';
import { Card } from '@/components/ui/card';
import {
  CameraIcon,
  CircleBackslashIcon,
  GearIcon,
  MaskOffIcon,
  MixerVerticalIcon,
  ResetIcon,
} from '@radix-ui/react-icons';
import { ReactNode, useMemo, useState } from 'react';
import { ShieldIcon } from '@/components/ShieldIcon';
import { ManageBansButton } from '@/components/bans/ManageBansLink';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import {
  parsePhotoCarouselAdminView,
  PhotoCarouselAdminView,
} from '@/engagements/photo-carousel/admin/adminViewPreference';
import { PhotoCarouselViewToggle } from '@/engagements/photo-carousel/admin/SubmissionsToolbar';
import { PhotoSubmissionsPanel } from '@/engagements/photo-carousel/admin/PhotoSubmissionsPanel';
import {
  DeletePhotoDialog,
  PhotoSubmissionModel,
} from '@/engagements/photo-carousel/admin/PhotoSubmission';
import {
  PhotoSubmissionFilter,
  getPhotoSubmissionStatus,
  matchesPhotoSubmissionFilter,
} from '@/engagements/photo-carousel/admin/submissionStatus';

function photoSvg(markup: string) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="720">${markup}</svg>`,
  )}`;
}

const PHOTO_WALLABY = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#7c3a6a"/><stop offset="1" stop-color="#2a1028"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/><circle cx="175" cy="300" r="60" fill="#f0a56a"/><circle cx="305" cy="300" r="60" fill="#e8b86d"/>`,
);
const PHOTO_GUEST = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#2dd4bf"/><stop offset="1" stop-color="#115e59"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/>`,
);
const PHOTO_OTTER = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#c084fc"/><stop offset="1" stop-color="#7c3aed"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/>`,
);
const PHOTO_FLAMINGO = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#fb923c"/><stop offset="1" stop-color="#ea580c"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/>`,
);
const PHOTO_ZANY = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#1e293b"/><stop offset="1" stop-color="#0f172a"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/><circle cx="210" cy="340" r="90" fill="#334155"/>`,
);
const PHOTO_CAPY = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#2dd4bf"/><stop offset="1" stop-color="#134e4a"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/>`,
);
const PHOTO_WOMBAT = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#7c3a6a"/><stop offset="1" stop-color="#2a1028"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/><circle cx="175" cy="300" r="60" fill="#f0a56a"/><circle cx="305" cy="300" r="60" fill="#e8b86d"/>`,
);
const PHOTO_SASSY_DARK = photoSvg(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#1e293b"/><stop offset="1" stop-color="#020617"/></linearGradient></defs><rect width="480" height="720" fill="url(#g)"/>`,
);

const FIXTURE_BANS: BanFieldsFragment[] = [
  {
    __typename: 'Ban',
    id: 'ban_1',
    userId: 'cm1fzq7xuser',
    reason: 'Inappropriate photo',
    createdAt: '2026-09-26T21:43:00.000Z',
    hiddenCount: 3,
    sourceEventId: 'evt_1',
    sourceEngagementId: 'eng_1',
    sourceSubmissionId: 'sub_1',
    sourcePhotoUrl: PHOTO_ZANY,
    user: { __typename: 'User', id: 'cm1fzq7xuser', name: 'Zany Quokka' },
    sourceEvent: {
      __typename: 'Event',
      id: 'evt_1',
      name: 'Demo Night @ Sample Hall',
    },
    sourceEngagement: {
      __typename: 'Engagement',
      id: 'eng_1',
      title: 'Photo Carousel',
    },
  },
  {
    __typename: 'Ban',
    id: 'ban_2',
    userId: 'cm1fsn3kuser',
    reason: null,
    createdAt: '2026-09-26T20:15:00.000Z',
    hiddenCount: 0,
    sourceEventId: 'evt_1',
    sourceEngagementId: 'eng_1',
    sourceSubmissionId: 'sub_2',
    sourcePhotoUrl: PHOTO_ZANY,
    user: { __typename: 'User', id: 'cm1fsn3kuser', name: 'Sassy Narwhal' },
    sourceEvent: {
      __typename: 'Event',
      id: 'evt_1',
      name: 'Demo Night @ Sample Hall',
    },
    sourceEngagement: {
      __typename: 'Engagement',
      id: 'eng_1',
      title: 'Photo Carousel',
    },
  },
  {
    __typename: 'Ban',
    id: 'ban_3',
    userId: 'cm1ftw5duser',
    reason: 'Spamming votes',
    createdAt: '2026-09-12T22:02:00.000Z',
    hiddenCount: 0,
    sourceEventId: 'evt_2',
    sourceEngagementId: 'eng_2',
    sourceSubmissionId: null,
    sourcePhotoUrl: null,
    user: { __typename: 'User', id: 'cm1ftw5duser', name: 'Test Wombat' },
    sourceEvent: {
      __typename: 'Event',
      id: 'evt_2',
      name: 'Sample Fest (Demo)',
    },
    sourceEngagement: {
      __typename: 'Engagement',
      id: 'eng_2',
      title: 'Best Costume (Live Poll)',
    },
  },
  {
    __typename: 'Ban',
    id: 'ban_4',
    userId: 'anonuser_a1b2',
    reason: null,
    createdAt: '2026-09-10T18:22:00.000Z',
    hiddenCount: 1,
    sourceEventId: 'evt_1',
    sourceEngagementId: 'eng_1',
    sourceSubmissionId: 'sub_4',
    sourcePhotoUrl: null,
    user: { __typename: 'User', id: 'anonuser_a1b2', name: null },
    sourceEvent: {
      __typename: 'Event',
      id: 'evt_1',
      name: 'Demo Night @ Sample Hall',
    },
    sourceEngagement: {
      __typename: 'Engagement',
      id: 'eng_1',
      title: 'Photo Carousel',
    },
  },
];

const FIXTURE_SUBMISSIONS: PhotoSubmissionModel[] = [
  {
    id: 'sub_pending_named',
    photoUrl: PHOTO_WALLABY,
    caption: 'Confetti cannon!!',
    createdAt: '2026-09-27T02:42:00.000Z',
    approved: null,
    user: { id: 'cm1perkywallaby', name: 'Perky Wallaby' },
    userId: 'cm1perkywallaby',
  },
  {
    id: 'sub_pending_guest',
    photoUrl: PHOTO_GUEST,
    caption: '',
    createdAt: '2026-09-27T02:41:00.000Z',
    approved: null,
    user: { id: 'anonuser_7f3a', name: null },
    userId: 'anonuser_7f3a',
  },
  {
    id: 'sub_approved_otter',
    photoUrl: PHOTO_OTTER,
    caption: 'Front row with the crew',
    createdAt: '2026-09-27T02:39:00.000Z',
    approved: true,
    user: { id: 'cm1jollyotter', name: 'Jolly Otter' },
    userId: 'cm1jollyotter',
  },
  {
    id: 'sub_pending_flamingo',
    photoUrl: PHOTO_FLAMINGO,
    caption: 'Dance floor is ON FIRE tonight, everybody get up here',
    createdAt: '2026-09-27T02:37:00.000Z',
    approved: null,
    user: {
      id: 'cm1flamingo',
      name: 'Magnificently Sparkly Disco Flamingo Supreme',
    },
    userId: 'cm1flamingo',
  },
  {
    id: 'sub_rejected',
    photoUrl: PHOTO_ZANY,
    caption: 'lol look at this',
    createdAt: '2026-09-27T02:35:00.000Z',
    approved: false,
    user: { id: 'cm1zanyquokka', name: 'Zany Quokka' },
    userId: 'cm1zanyquokka',
    possibleBannedDevice: true,
  },
  {
    id: 'sub_approved_capy',
    photoUrl: PHOTO_CAPY,
    caption: 'Hi mom 👋',
    createdAt: '2026-09-27T02:33:00.000Z',
    approved: true,
    user: { id: 'cm1bouncycapy', name: 'Bouncy Capybara' },
    userId: 'cm1bouncycapy',
  },
  {
    id: 'sub_approved_wombat',
    photoUrl: PHOTO_WOMBAT,
    caption: 'Best night ever',
    createdAt: '2026-09-27T02:30:00.000Z',
    approved: true,
    user: { id: 'cm1testwombat', name: 'Test Wombat' },
    userId: 'cm1testwombat',
  },
  {
    id: 'sub_rejected_sassy',
    photoUrl: PHOTO_SASSY_DARK,
    caption: '',
    createdAt: '2026-09-27T02:28:00.000Z',
    approved: false,
    user: { id: 'cm1sassynarwhal', name: 'Sassy Narwhal' },
    userId: 'cm1sassynarwhal',
  },
];

type PreviewTab = 'control' | 'moderation';

function parseStatusFilter(raw: string | null): PhotoSubmissionFilter {
  if (raw === 'pending' || raw === 'approved' || raw === 'rejected') {
    return raw;
  }
  return 'all';
}

export default function BanUiPreviewPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialTab = useMemo<PreviewTab>(() => {
    return params.get('tab') === 'moderation' ? 'moderation' : 'control';
  }, [params]);
  const [tab, setTab] = useState<PreviewTab>(initialTab);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(
    params.get('preview') === '1' ? 'sub_pending_guest' : null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [view, setView] = useState<PhotoCarouselAdminView>(
    parsePhotoCarouselAdminView(params.get('view')),
  );
  const [filter, setFilter] = useState<PhotoSubmissionFilter>(
    parseStatusFilter(params.get('status')),
  );
  const [moreMenuId, setMoreMenuId] = useState<string | null>(
    params.get('menu') === '1'
      ? view === 'list'
        ? 'sub_rejected'
        : 'sub_pending_named'
      : null,
  );
  const [unbanOpenDefault] = useState(params.get('unban') === '1');

  const counts = useMemo(() => {
    const next = { all: 0, pending: 0, approved: 0, rejected: 0 };
    for (const submission of FIXTURE_SUBMISSIONS) {
      const status = getPhotoSubmissionStatus(submission.approved);
      next.all += 1;
      next[status] += 1;
    }
    return next;
  }, []);

  const visibleSubmissions = useMemo(
    () =>
      FIXTURE_SUBMISSIONS.filter((submission) =>
        matchesPhotoSubmissionFilter(
          getPhotoSubmissionStatus(submission.approved),
          filter,
        ),
      ),
    [filter],
  );

  return (
    <div
      data-name="SCREEN_ADMIN"
      className="flex min-h-screen flex-col bg-background pb-32 text-foreground md:pb-0"
    >
      <header className="flex h-14 items-center justify-between px-4">
        <span className="font-heading text-2xl">HappiScreen</span>
        <span className="text-sm text-muted-foreground">Admin</span>
      </header>
      <div className="flex flex-row items-end justify-between border-b md:px-6 md:pt-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'sm' }),
              'inline-flex items-center gap-2',
            )}
          >
            <ResetIcon />
            <span>back</span>
          </span>
          <h4 className="whitespace-nowrap p-2 text-lg md:p-0">
            Demo Night @ Sample Hall
          </h4>
        </div>
        <nav
          data-name="SCREEN_ADMIN_NAV"
          className={cn(
            'fixed bottom-0 left-0 right-0 z-10 flex h-[7vh] w-full items-center justify-center space-x-2 border-t bg-background md:relative md:bottom-auto md:h-auto md:w-auto md:border-t-0 md:bg-transparent',
          )}
        >
          <PreviewTabLink
            active={false}
            icon={<GearIcon className="size-6 md:size-4" />}
            label="General"
          />
          <PreviewTabLink
            active={false}
            icon={<MaskOffIcon className="size-6 md:size-4" />}
            label="Editor"
          />
          <PreviewTabLink
            active={tab === 'control'}
            icon={<MixerVerticalIcon className="size-6 md:size-4" />}
            label="Control"
            onClick={() => setTab('control')}
          />
          <PreviewTabLink
            active={tab === 'moderation'}
            icon={<ShieldIcon className="size-6 md:size-4" />}
            label="Moderation"
            onClick={() => setTab('moderation')}
          />
        </nav>
      </div>

      <main className="flex flex-1 md:p-4">
        {tab === 'control' ? (
          <div
            data-name="PHOTO_CAROUSEL_ADMIN_CONTROL_VIEW"
            className="flex h-full w-full items-start justify-start"
          >
            <div className="flex w-full flex-col justify-start gap-4 overflow-auto p-4">
              <div className="flex items-start justify-between gap-3">
                <h2 className="flex min-w-0 flex-row items-center gap-2 text-2xl font-bold">
                  <CameraIcon className="h-8 w-8 shrink-0" />
                  <span className="truncate">Photo Carousel</span>
                </h2>
                <div className="flex shrink-0 items-center gap-2 lg:hidden">
                  <PhotoCarouselViewToggle
                    value={view}
                    onChange={setView}
                    iconOnly
                  />
                </div>
                <div className="hidden pt-1 lg:block">
                  <ManageBansButton
                    count={FIXTURE_BANS.length}
                    onClick={() => setTab('moderation')}
                  />
                </div>
              </div>
              <div className="lg:hidden">
                <ManageBansButton
                  count={FIXTURE_BANS.length}
                  onClick={() => setTab('moderation')}
                />
              </div>
              <PhotoSubmissionsPanel
                view={view}
                onViewChange={setView}
                filter={filter}
                onFilterChange={setFilter}
                counts={counts}
                submissions={visibleSubmissions}
                bansEnabled
                requireApproval
                newIds={new Set(['sub_pending_named'])}
                previewId={previewId}
                onPreviewIdChange={setPreviewId}
                moreMenuId={moreMenuId}
                onMoreMenuIdChange={setMoreMenuId}
                onApprove={() => undefined}
                onReject={() => undefined}
                onRequestDelete={() => setDeleteOpen(true)}
                onBan={() => setDialogOpen(true)}
              />
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-screen-lg p-4">
            <div className="flex items-center gap-2 px-1 pb-2">
              <CircleBackslashIcon className="h-5 w-5" />
              <h2 className="text-xl font-semibold">
                Banned people{' '}
                <span className="text-muted-foreground">
                  {FIXTURE_BANS.length}
                </span>
              </h2>
            </div>
            <p className="px-1 pb-6 text-sm text-muted-foreground">
              Bans apply to{' '}
              <span className="font-semibold">all your screens</span>, including
              future ones. Banned people can&apos;t submit or vote anywhere.
              This list is the same on every screen&apos;s Moderation tab.
            </p>
            <Card className="overflow-hidden px-4">
              <BannedPeopleList
                bans={FIXTURE_BANS}
                defaultUnbanOpenId={
                  unbanOpenDefault ? FIXTURE_BANS[0].id : undefined
                }
                onUnban={async () => undefined}
              />
            </Card>
          </div>
        )}
      </main>

      <BanPersonDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        name="Zany Quokka"
        userId="cm1fzq7xuser"
        photoUrl={PHOTO_ZANY}
        submissionCount={3}
        onConfirm={async () => undefined}
      />
      <DeletePhotoDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={() => undefined}
      />
    </div>
  );
}

function PreviewTabLink({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        active
          ? 'bg-epic text-epic-foreground md:border-border md:bg-background md:text-foreground'
          : 'border-transparent text-muted-foreground hover:bg-transparent',
        'relative top-[1px] flex h-full items-center justify-start border-l border-r border-t px-4 hover:text-foreground md:rounded-tl-lg md:rounded-tr-lg md:py-2',
      )}
    >
      <div className="flex flex-row items-center space-x-2">
        {icon}
        <div className="hidden lg:block">{label}</div>
      </div>
    </button>
  );
}
